/**
 * @module ProductLayers
 */
import { CurrentStormsLayer } from './layers/CurrentStormsLayer.js';
import { GeojsonLayer } from './layers/GeojsonLayer.js';
import { KmzLayer } from './layers/KmzLayer.js';
import { getLayerByName } from './utils/getLayerByName.js';

class ProductLayers {
  constructor(map, language) {
    this.map = map;
    this.language = language;
  }

  /**
   * Set the language for the product manager
   * @param {string} newLanguage - The new language code (e.g., 'en', 'es')
   */
  setLanguage(newLanguage) {
    this.language = newLanguage;
  }

  /**
   * Adds a layer to the map.
   * @param {object} layerConfig 
   * @param {string} productId 
   * @returns 
   */
  async addLayer(layerConfig, productId) {
    let layer;
    /* GeoJSON Layers */
    if (layerConfig.type === 'GeoJSON') {
      const geojsonLayer = new GeojsonLayer(layerConfig, productId);
      layer = await geojsonLayer.loadLayer(this.language);
    }
    /* CurrentStorms Layer */
    else if (layerConfig.type === "JSON") {
      const currentStormsLayer = new CurrentStormsLayer(layerConfig, productId);
      layer = await currentStormsLayer.loadLayer();
    }
    /* KMZ Layer */
    else if (layerConfig.type === "KMZ") {
      const kmzLayer = new KmzLayer(layerConfig, productId);
      layer = await kmzLayer.loadLayer();
    }
    // Add other layer types as needed
    if (!layer) {
      return;
    }
    layer.set('name', layerConfig.id);
    this.map.addLayer(layer);
  }

  async addLayers(layersConfig, productId) {
    for (const layerConfig of layersConfig) {
      await this.addLayer(layerConfig, productId);
    }
  }

  removeLayer(layerConfig) {
    const layer = getLayerByName(this.map, layerConfig.id);
    if (layer) {
      this.map.removeLayer(layer);
    }
  }

  removeLayers(layersConfig) {
    for (const layerConfig of layersConfig) {
      this.removeLayer(layerConfig);
    }
  }

  async updateLayer(layerConfig, productId) {
    const layer = getLayerByName(this.map, layerConfig.id);
    if (layer) {
      if (layerConfig.type === 'GeoJSON') {
        const geojsonLayer = new GeojsonLayer(layerConfig, productId);
        await geojsonLayer.updateLayer(layer, this.language);
      } 
      else if (layerConfig.type === 'JSON') {
        const currentStormsLayer = new CurrentStormsLayer(layerConfig, productId);
        await currentStormsLayer.updateLayer(layer);
      }
      else if (layerConfig.type === 'KMZ') {
        const kmzLayer = new KmzLayer(layerConfig, productId);
        await kmzLayer.updateLayer(layer);
      }
    }
  }

  async updateLayers(layersConfig, productId) {
    for (const layerConfig of layersConfig) {
      await this.updateLayer(layerConfig, productId);
    }
  }

}

export default ProductLayers;